import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import dayjs from "dayjs";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, FileText, Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DiseaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get(`/diseases/${id}`).then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, [id]);

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const { disease, blogs } = data;

  const removeBlog = async (bid) => {
    if (!window.confirm("Delete this blog?")) return;
    await api.delete(`/blogs/${bid}`);
    toast.success("Blog deleted");
    load();
  };

  return (
    <div className="space-y-6 fade-up">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="disease-back-btn"
      >
        <ArrowLeft className="size-4" /> Back to diseases
      </button>

      <Card className="p-6">
        <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">Disease</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">{disease.name}</h1>
        {disease.symptoms && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest text-secondary font-bold">
              Symptoms
            </p>
            <p className="text-foreground/80 mt-1">{disease.symptoms}</p>
          </div>
        )}
        {disease.description && (
          <p className="mt-4 text-foreground/80 leading-relaxed">{disease.description}</p>
        )}
      </Card>

      <div className="flex items-baseline justify-between">
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Tagged blogs</h2>
        <Link to={`/blog/new?category=disease&disease_id=${id}`}>
          <Button data-testid="add-disease-blog-btn">
            <Plus className="size-4 mr-2" /> New Blog
          </Button>
        </Link>
      </div>

      {blogs.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <FileText className="size-7 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No blogs tagged to this disease yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {blogs.map((b) => (
            <Card key={b.id} className="p-5" data-testid={`disease-blog-${b.id}`}>
              <div className="flex items-start gap-3 mb-2">
                <Badge variant="outline" className="capitalize">{b.category}</Badge>
                {b.status === "draft" && (
                  <Badge variant="secondary" className="capitalize">Draft</Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {dayjs(b.created_at).format("MMM D, YYYY")}
                </span>
              </div>
              <p className="font-heading text-lg font-semibold">{b.title}</p>
              {b.excerpt && (
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{b.excerpt}</p>
              )}
              <div className="flex gap-2 mt-4">
                <Link to={`/blog/${b.id}`}>
                  <Button variant="outline" size="sm" data-testid={`edit-blog-${b.id}`}>
                    <Edit2 className="size-3.5 mr-1" /> Edit
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeBlog(b.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`del-blog-${b.id}`}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
